import { Printer } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import entete from "../../../assets/img/entete_pool.png";
import { Checkbox, Image, Input, InputNumber, Select } from 'antd';
import BarreTop from '../../../components/docs/BarreTop';
import { DocumentFooter } from '../../../components/docs/DocumentFooter';
import { frenchDate, numberToWordsFrench } from '../../../variables/functions';
import { normaliserGaranties } from '../../../variables/stringFunctions';
import { getCompagnieLogo } from '../../../hooks/useFilesHooks';
import { useReactToPrint } from 'react-to-print';
import axiosInstance from '../../../variables/api/axiosInstance';

/**
 * PRISE EN CHARGE MATÉRIEL — nouveau format COMPACT (1 page), style « bon de réparation »
 * remis au garage. Implémenté EN PARALLÈLE du format existant (PriseEnCharge.js).
 * Impression sur papier à en-tête (en-tête/pied masqués mais hauteur réservée) ou non.
 */
const RED = '#b91c1c';
const fmt = (v) => `${Number(v || 0).toLocaleString('fr-FR')} FCFA`;

const PriseEnChargeMateriel = (props) => {
    const sinistre = props?.sinistre;
    const pec = props?.priseEnCharge;
    const printRef = useRef(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    const [organisme, setOrganisme] = useState(sinistre?.contrat?.organisme || {});
    const [afficherEnteteEtPied, setAfficherEnteteEtPied] = useState(props?.afficherEnteteEtPied !== false);
    // Valeur à neuf SAISISSABLE (pré-remplie depuis l'engin si connue). Vide → tiret sur le document.
    const [valeurANeuf, setValeurANeuf] = useState(null);
    // Valeur vénale SAISISSABLE de la même façon (pré-remplie depuis l'engin). Vide → tiret.
    const [valeurVenale, setValeurVenale] = useState(null);
    // Garantie(s) EN JEU sélectionnée(s) (menu). Pré-remplies avec les garanties déduites du dossier.
    const [garantiesSelection, setGarantiesSelection] = useState([]);
    // Date de 1re mise en circulation SAISISSABLE. Défaut : celle de l'engin, sinon l'ANNÉE de la date
    // d'effet du contrat. Vide → tiret.
    const [miseCirculation, setMiseCirculation] = useState('');

    useEffect(() => {
        const code = sinistre?.contrat?.organisme?.code;
        if (!code) return;
        axiosInstance.get(`/api/organisme/code/${code}`)
            .then(({ data }) => { if (data) setOrganisme(data); })
            .catch((err) => console.log(err));
    }, [sinistre?.contrat?.organisme?.code]);

    useEffect(() => {
        if (sinistre?.contrat?.organisme?.typeOrganisme !== "POOL") {
            getCompagnieLogo(sinistre?.contrat?.organisme?.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    const blob = new Blob([imageBytes], { type: "image/png" });
                    setLogoPreviewUrl(URL.createObjectURL(blob));
                })
                .catch((error) => console.log(error));
        }
    }, []);

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Prise_en_charge_materiel_${pec?.numeroPriseEnCharge || ''}`,
        pageStyle: `@page { size: A4 portrait; margin: 0; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .doc-toolbar { display: none !important; } }`,
    });

    // ── Données ─────────────────────────────────────────────────────────
    const etablissementNom = pec?.etablissement?.nom || '……………';
    const souscripteur = sinistre?.contrat?.souscripteur;
    const assureNom = `${souscripteur?.nom || ''} ${souscripteur?.prenom || ''}`.trim() || '……………';
    const police = sinistre?.contrat?.numeroPoliceP || sinistre?.contrat?.numeroPoliceM || '……………';
    const numSinistre = sinistre?.numSinistre || sinistre?.numeroSinistreM || '……………';
    const dateAccident = sinistre?.dateSinistre ? frenchDate(sinistre.dateSinistre) : '……………';

    // Véhicule à réparer = engin du bénéficiaire (partie adverse), repli sur l'engin du sinistre.
    const engin = pec?.victime?.engin || sinistre?.engin || {};

    // Garanties concernées : celles DÉCLARÉES sur le sinistre ; à défaut (dossier non renseigné),
    // les garanties du véhicule à réparer (sous-garanties de l'engin). Normalisées (variantes RC →
    // « RC ») et dédupliquées.
    const garantiesConcernees = (() => {
        const declarees = Array.isArray(sinistre?.garanties) ? sinistre.garanties : [];
        const source = declarees.length
            ? declarees
            : (Array.isArray(engin?.sousGaranties)
                ? engin.sousGaranties.map((sg) => sg?.nom || sg?.garantie?.nom)
                : []);
        return normaliserGaranties(source);
    })();
    // Options du menu « garantie en jeu » : garanties déduites du dossier + sous-garanties de l'engin.
    const optionsGaranties = (() => {
        const fromEngin = Array.isArray(engin?.sousGaranties)
            ? engin.sousGaranties.map((sg) => sg?.nom || sg?.garantie?.nom) : [];
        return normaliserGaranties([...garantiesConcernees, ...fromEngin]).map((g) => ({ label: g, value: g }));
    })();
    // Ligne GARANTIE(S) affichée = sélection du menu ; à défaut, les garanties déduites.
    const garantie = garantiesSelection.length
        ? garantiesSelection.join(', ')
        : (garantiesConcernees.length ? garantiesConcernees.join(', ') : (sinistre?.garantie || '……………'));
    const proprietairePersonne = pec?.victime?.personne;
    const proprietaireNom = proprietairePersonne
        ? `${proprietairePersonne?.nom || ''} ${proprietairePersonne?.prenom || ''}`.trim()
        : assureNom;
    // Ligne mise en circulation affichée = saisie (défaut : engin, sinon année de la date d'effet).
    const dateMEC = miseCirculation;

    // Pré-remplit la sélection des garanties avec celles déduites du dossier (une seule fois par dossier).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setGarantiesSelection(garantiesConcernees); }, [garantiesConcernees.join('|')]);

    // Pré-remplit la date de 1re mise en circulation : celle de l'engin si connue, sinon l'ANNÉE de la
    // date d'effet du contrat.
    useEffect(() => {
        if (engin?.dateMiseEnCirculation) {
            setMiseCirculation(frenchDate(engin.dateMiseEnCirculation));
        } else if (sinistre?.contrat?.dateEffet) {
            setMiseCirculation(String(new Date(sinistre.contrat.dateEffet).getFullYear()));
        }
    }, [engin?.dateMiseEnCirculation, sinistre?.contrat?.dateEffet]);

    // Pré-remplit la valeur à neuf avec celle de l'engin (modifiable ; l'utilisateur peut la vider → tiret).
    useEffect(() => {
        if (engin?.valeurANeuf != null) setValeurANeuf(engin.valeurANeuf);
    }, [engin?.valeurANeuf]);

    // Idem valeur vénale : pré-remplie depuis l'engin, saisissable dans la barre d'outils.
    useEffect(() => {
        if (engin?.valeurVenale != null) setValeurVenale(engin.valeurVenale);
    }, [engin?.valeurVenale]);

    const charges = Array.isArray(pec?.charge) ? pec.charge : [];
    // Montant affiché par ligne = montant RETENU (montant réellement pris en charge).
    const montantLigne = (c) => Number(c?.montantRetenue) || 0;
    const estLigneTotal = (c) => /TOTAL|MONTANT/i.test(c?.acte || '');
    const montantTotal = Number(pec?.montant) || charges.reduce((s, c) => s + montantLigne(c), 0);

    // ── Styles ──────────────────────────────────────────────────────────
    const pageStyle = {
        width: '210mm', minHeight: '296mm', maxHeight: '296mm', overflow: 'hidden',
        boxSizing: 'border-box', padding: '10mm 16mm',
        display: 'flex', flexDirection: 'column', backgroundColor: 'white',
        fontFamily: '"Times New Roman", Georgia, serif', fontSize: '13px', color: '#000', lineHeight: 1.35,
    };
    const sectionTitle = { fontWeight: 'bold', fontStyle: 'italic', textDecoration: 'underline', margin: '8px 0 2px 0' };
    const indent = { paddingLeft: '34px' };
    const ligneMontant = (bold) => ({
        display: 'flex', justifyContent: 'space-between', maxWidth: '520px',
        fontWeight: bold ? 'bold' : 'normal', padding: '0.5px 0',
    });

    const Header = () => (
        afficherEnteteEtPied ? (
            <div style={{ flexShrink: 0, marginBottom: '4px' }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {organisme?.typeOrganisme !== "POOL" && (
                        <Image src={logoPreviewUrl} preview={false} style={{ width: "auto", height: "58px" }} />
                    )}
                    <Image src={entete} preview={false} style={{ width: "auto", height: "58px" }} />
                </div>
                <BarreTop />
            </div>
        ) : (
            <div style={{ flexShrink: 0, height: '70px', marginBottom: '4px' }} />
        )
    );

    const Footer = () => (
        afficherEnteteEtPied ? (
            <div style={{ marginTop: 'auto', paddingTop: '6px' }}><DocumentFooter /></div>
        ) : (
            <div style={{ marginTop: 'auto', paddingTop: '6px', height: '44px' }} />
        )
    );

    return (
        <div style={{ padding: '20px', backgroundColor: '#f5f5f5' }}>
            <div className="doc-toolbar" style={{ marginBottom: '16px', display: 'flex', gap: '10px', rowGap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                    onClick={handlePrint}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#242166', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
                >
                    <Printer size={18} /> Imprimer
                </button>
                <Checkbox
                    checked={!afficherEnteteEtPied}
                    onChange={(e) => setAfficherEnteteEtPied(!e.target.checked)}
                >
                    Imprimer sur papier à en-tête
                </Checkbox>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, color: '#555' }}>Valeur à neuf :</span>
                    <InputNumber
                        value={valeurANeuf}
                        onChange={(v) => setValeurANeuf(v)}
                        min={0}
                        controls={false}
                        placeholder="—"
                        style={{ width: 150 }}
                        formatter={(v) => (v == null || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' '))}
                        parser={(v) => `${v}`.replace(/\s/g, '')}
                    />
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, color: '#555' }}>Valeur vénale :</span>
                    <InputNumber
                        value={valeurVenale}
                        onChange={(v) => setValeurVenale(v)}
                        min={0}
                        controls={false}
                        placeholder="—"
                        style={{ width: 150 }}
                        formatter={(v) => (v == null || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' '))}
                        parser={(v) => `${v}`.replace(/\s/g, '')}
                    />
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, color: '#555' }}>Mise en circulation :</span>
                    <Input
                        value={miseCirculation}
                        onChange={(e) => setMiseCirculation(e.target.value)}
                        allowClear
                        placeholder="—"
                        style={{ width: 130 }}
                    />
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, color: '#555' }}>Garantie(s) en jeu :</span>
                    <Select
                        mode="tags"
                        value={garantiesSelection}
                        onChange={(v) => setGarantiesSelection(v)}
                        options={optionsGaranties}
                        placeholder="Sélectionner"
                        style={{ minWidth: 180, maxWidth: 300 }}
                        maxTagCount="responsive"
                    />
                </span>
            </div>

            <div ref={printRef} className="pec-materiel" style={{ backgroundColor: 'white', width: '210mm', margin: '0 auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                {/* Force les gras : neutralise le « * { font-weight: normal !important } » global (toPrint.css)
                    qui écrasait les gras à l'impression. */}
                <style>{`
                    .pec-materiel strong, .pec-materiel b,
                    .pec-materiel .pec-title, .pec-materiel .pec-sub,
                    .pec-materiel .pec-amt, .pec-materiel .pec-sign { font-weight: bold !important; }
                `}</style>
                <div style={pageStyle}>
                    <Header />

                    <div style={{ flex: 1 }}>
                        {/* Titre encadré */}
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                            <div className="pec-title" style={{ border: '2px solid #000', borderRadius: '14px', padding: '6px 26px', textAlign: 'center', fontWeight: 'bold', fontSize: '17px', textTransform: 'uppercase' }}>
                                Prise en charge matériel<br />délivrée à : {etablissementNom}
                            </div>
                        </div>

                        {/* CONTRAT */}
                        <div className="pec-sub" style={sectionTitle}>• CONTRAT :</div>
                        <div style={indent}>
                            <div>ASSURE : <strong>{assureNom}</strong></div>
                            <div>POLICE : {police}</div>
                            <div>GARANTIE(S) : <strong>{garantie}</strong></div>
                        </div>

                        {/* SINISTRE */}
                        <div className="pec-sub" style={sectionTitle}>• SINISTRE :</div>
                        <div style={indent}>
                            <div>SINISTRE N° : {numSinistre}</div>
                            <div>DATE DE L'ACCIDENT : {dateAccident}</div>
                        </div>

                        {/* VEHICULE A REPARER */}
                        <div className="pec-sub" style={sectionTitle}>• VÉHICULE À RÉPARER :</div>
                        <div style={indent}>
                            <div>PROPRIÉTAIRE : <strong>{proprietaireNom || '……………'}</strong></div>
                            <div>MARQUE / TYPE : <strong>{engin?.marque || '……………'}</strong></div>
                            <div>IMMATRICULATION : <strong>{engin?.immatriculation || '……………'}</strong></div>
                            <div>DATE DE PREMIÈRE MISE EN CIRCULATION : {dateMEC || '-'}</div>
                            <div>VALEUR À NEUF : {valeurANeuf ? fmt(valeurANeuf) : '-'}</div>
                            <div>VALEUR VÉNALE : {valeurVenale ? fmt(valeurVenale) : '-'}</div>
                        </div>

                        {/* DETAIL */}
                        <div className="pec-sub" style={sectionTitle}>• DÉTAIL DE LA PRISE EN CHARGE :</div>
                        <div style={{ ...indent, marginTop: '4px' }}>
                            {charges.map((c, i) => (
                                <div key={c?.codeId || i} className={estLigneTotal(c) ? 'pec-amt' : undefined} style={ligneMontant(estLigneTotal(c))}>
                                    <span>{c?.acte} :</span>
                                    <span>{fmt(montantLigne(c))}</span>
                                </div>
                            ))}
                            <div className="pec-amt" style={{ ...ligneMontant(true), borderTop: '1px solid #000', marginTop: '3px', paddingTop: '3px' }}>
                                <span>MONTANT PRIS EN CHARGE :</span>
                                <span>{fmt(montantTotal)}</span>
                            </div>
                        </div>

                        {/* Montant en toutes lettres ET en chiffres (en gras) */}
                        <div className="pec-amt" style={{ marginTop: '10px', fontWeight: 'bold', textDecoration: 'underline' }}>
                            MONTANT DE LA PRISE EN CHARGE : {Number(montantTotal).toLocaleString('fr-FR')} FTTC
                        </div>
                        <div className="pec-amt" style={{ textAlign: 'center', color: RED, fontWeight: 'bold', textTransform: 'uppercase' }}>
                            ({numberToWordsFrench(montantTotal)} FRANCS TTC)
                        </div>

                        {/* Notes */}
                        <div style={{ marginTop: '12px', textAlign: 'justify', fontWeight: 'bold' }}>
                            Joindre à la facture à payer la présente prise en charge ainsi que le bon de livraison
                            dûment signé par le propriétaire du véhicule réparé, pour permettre au POOL TPVM-VT GIE
                            de procéder au règlement.
                        </div>
                        <div style={{ marginTop: '10px', textAlign: 'justify', textDecoration: 'underline' }}>
                            Par ailleurs, nous vous informons que toutes les pièces remplacées doivent être mises à la
                            disposition du POOL TPVM-VT GIE.
                        </div>

                        {/* Signature — RAPPROCHÉE du corps (dans le flux, plus poussée en bas), date +
                            signataire en gras. */}
                        <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', textAlign: 'left' }}>
                            <div>
                                <div className="pec-sign" style={{ marginBottom: '64px' }}>Fait à Lomé, le {frenchDate(pec?.createdAt) || frenchDate(new Date())}</div>
                                <div className="pec-sign" style={{ textDecoration: 'underline' }}>{organisme?.responsable || ''}</div>
                                <div className="pec-sign">Directeur</div>
                            </div>
                        </div>
                    </div>

                    <Footer />
                </div>
            </div>

            <style>{`
                @media print {
                    body { margin: 0; padding: 0; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .doc-toolbar { display: none !important; }
                }
            `}</style>
        </div>
    );
};

PriseEnChargeMateriel.propTypes = {
    priseEnCharge: PropTypes.object,
    sinistre: PropTypes.object,
    afficherEnteteEtPied: PropTypes.bool,
};

export default PriseEnChargeMateriel;
